import PersonAddAltIcon from '@mui/icons-material/PersonAddAlt';
import { useAtom } from 'jotai';
import { Dispatch, SetStateAction } from 'react';
import { SearchInput, TooltipStyled } from 'src/components';
import { useCurrentUser, useGetOrgUsers } from 'src/hooks';
import {
  orgDebounceDelayAtom,
  orgFilterByAtom,
  orgSearchByAtom,
  orgSortByAtom,
  orgTotalUsersAtom,
} from 'src/store';
import { FilterByDropdown, MobileDropdown, SortByDropdown } from '.';

type Props = {
  setShowInviteModal: Dispatch<SetStateAction<boolean>>;
};

export const Header = ({ setShowInviteModal }: Props) => {
  const { user } = useCurrentUser();
  const [totalUsers] = useAtom(orgTotalUsersAtom);
  const [searchBy, setSearchBy] = useAtom(orgSearchByAtom);
  const [filterBy, setFilterBy] = useAtom(orgFilterByAtom);
  const [sortBy, setSortBy] = useAtom(orgSortByAtom);
  const [, setDebounceDelay] = useAtom(orgDebounceDelayAtom);

  const usersQuery = useGetOrgUsers();

  const disableAddUsersButton =
    user?.subscription.role !== 'admin' &&
    user?.subscription.role !== 'approver';

  const handleEnterPress = () => {
    setDebounceDelay(0);
    setTimeout(() => {
      setDebounceDelay(500);
    }, 20);
  };

  const handleSearchChange = (value: string) => {
    if (value.length === 0 && searchBy !== '') {
      handleEnterPress();
    }
    setSearchBy(value);
  };

  return (
    <div className='flex w-full flex-col items-center justify-center gap-8 bg-offWhite px-4 pt-4 sm:gap-10 sm:px-8 sm:pt-6 md:gap-10 md:px-12 md:pt-8 lg:gap-12'>
      <div className='flex w-full flex-row flex-wrap items-center justify-between gap-2 gap-y-4 sm:gap-4 md:gap-6'>
        <div className='flex flex-row items-center justify-start gap-2 sm:gap-4 md:gap-6'>
          <div className='flex w-full flex-row  items-center justify-start gap-2 sm:gap-4 md:gap-6'>
            <span className='heading-200 whitespace-nowrap text-primary-dark'>
              {usersQuery.isLoading && !totalUsers
                ? 'Loading state'
                : `${totalUsers} Users`}
            </span>

            {/* Search Input */}
            <SearchInput
              value={searchBy}
              onChange={handleSearchChange}
              placeholder='Find a User'
              focusPlaceholder='Type to find a user'
              onEnterPress={handleEnterPress}
              className='w-[254px] min-w-[120px] max-w-[254px]'
            />
          </div>

          <div className='hidden gap-4 md:inline-flex'>
            <FilterByDropdown filterBy={filterBy} setFilterBy={setFilterBy} />
            <SortByDropdown sortBy={sortBy} setSortBy={setSortBy} />
          </div>

          <div className='md:hidden'>
            <MobileDropdown
              filterBy={filterBy}
              sortBy={sortBy}
              setFilterBy={setFilterBy}
              setSortBy={setSortBy}
            />
          </div>
        </div>

        <TooltipStyled
          title='Only admins and approvers can invite users'
          disableHoverListener={!disableAddUsersButton}
          disableTouchListener={!disableAddUsersButton}
          placement='top'
          slotProps={{
            tooltip: {
              style: { maxWidth: 'none' },
            },
          }}
        >
          <span>
            <button
              type='button'
              onClick={() => setShowInviteModal(true)}
              className='button-secondary ml-auto h-12 disabled:hover:cursor-auto'
              disabled={disableAddUsersButton}
            >
              <PersonAddAltIcon className='mr-2 text-primary' />
              Add Users
            </button>
          </span>
        </TooltipStyled>
      </div>
    </div>
  );
};
